import { supabase } from '@/lib/supabase';
import { trackSubscriptionEvent } from './subscriptionAnalytics';

/**
 * Error codes for affiliate operations
 */
export enum AffiliateErrorCode {
  VALIDATION_FAILED = 'VALIDATION_FAILED',
  SERVER_ERROR = 'SERVER_ERROR',
  INVALID_CODE = 'INVALID_CODE',
  ALREADY_ATTRIBUTED = 'ALREADY_ATTRIBUTED',
  SELF_REFERRAL = 'SELF_REFERRAL'
}

/**
 * Interface for affiliate errors
 */
export interface AffiliateError {
  code: AffiliateErrorCode;
  message: string;
  details?: any;
}

// Check if user has an existing affiliate request
export const checkAffiliateRequestStatus = async (userId: string): Promise<string | null> => {
  try {
    const { data, error } = await supabase
      .from('affiliate_requests')
      .select('status')
      .eq('user_id', userId)
      .order('created_at', { ascending: false })
      .maybeSingle();
      
    if (error) throw error;
    
    return data ? data.status : null;
  } catch (error) {
    console.error('Error checking affiliate request status:', error);
    return null;
  }
};

// Check if user can be an affiliate (based on user_roles)
export const checkCanBeAffiliate = async (userId: string): Promise<boolean> => {
  try {
    const { data, error } = await supabase
      .from('user_roles')
      .select('can_be_affiliate')
      .eq('user_id', userId)
      .maybeSingle();
      
    if (error) throw error;
    
    return !!data?.can_be_affiliate;
  } catch (error) {
    console.error('Error checking if user can be affiliate:', error);
    return false;
  }
};

// Check if user has an existing affiliate application
export const checkAffiliateApplication = async (userId: string): Promise<{status: string | null, referralCode: string | null}> => {
  try {
    const { data, error } = await supabase
      .from('affiliates')
      .select('status, referral_code')
      .eq('user_id', userId)
      .maybeSingle();
      
    if (error) throw error;
    
    return { 
      status: data ? data.status : null,
      referralCode: data ? data.referral_code : null
    };
  } catch (error) {
    console.error('Error checking affiliate application:', error);
    return { status: null, referralCode: null };
  }
};

// Submit a request to join the affiliate program
export const submitAffiliateRequest = async (userId: string, requestReason: string): Promise<boolean> => {
  try {
    const { error } = await supabase
      .from('affiliate_requests')
      .insert({
        user_id: userId,
        request_reason: requestReason,
        status: 'pending'
      });
      
    if (error) throw error;
    
    return true;
  } catch (error) {
    console.error('Error submitting affiliate request:', error);
    return false;
  }
};

/**
 * Store referral code in localStorage with additional metadata
 */
export const storeReferralCode = (referralCode: string): void => {
  try {
    if (!referralCode) return;
    
    // Standardize to lowercase for case-insensitive comparison
    const normalizedCode = referralCode.toLowerCase().trim();
    
    // Store the referral code
    localStorage.setItem('edaptica_referral_code', normalizedCode);
    
    // Set expiry for 30 days
    localStorage.setItem('edaptica_referral_expires', (Date.now() + 30 * 24 * 60 * 60 * 1000).toString());
    
    // Store additional metadata
    localStorage.setItem('edaptica_referral_source', window.location.href);
    localStorage.setItem('edaptica_referral_timestamp', Date.now().toString());
    
    console.log('Referral code stored successfully:', normalizedCode);
    
    // Track for analytics
    trackSubscriptionEvent('referral_stored', { 
      referral_code: normalizedCode,
      source_page: window.location.href,
      requires_auth: false
    });
  } catch (e) {
    console.error('Error storing referral code:', e);
  }
};

// Get stored referral code if it exists and hasn't expired
export const getStoredReferralCode = (): string | null => {
  try {
    const code = localStorage.getItem('edaptica_referral_code');
    const expires = localStorage.getItem('edaptica_referral_expires');
    
    if (!code || !expires) return null;
    
    // Check if expired
    if (Date.now() > parseInt(expires, 10)) {
      // Clean up expired referral data
      clearReferralData();
      return null;
    }
    
    return code;
  } catch (e) {
    console.error('Error getting stored referral code:', e);
    return null;
  }
};

/**
 * Clear all referral data from localStorage
 */
export const clearReferralData = (): void => {
  try {
    localStorage.removeItem('edaptica_referral_code');
    localStorage.removeItem('edaptica_referral_expires');
    localStorage.removeItem('edaptica_referral_source');
    localStorage.removeItem('edaptica_referral_timestamp');
  } catch (e) {
    console.error('Error clearing referral data:', e);
  }
};

/**
 * Validate if a referral code exists and is valid
 * @returns Promise<boolean> - true if valid, false otherwise
 */
export const validateReferralCode = async (referralCode: string): Promise<boolean> => {
  try {
    if (!referralCode || referralCode.trim() === '') {
      console.log('Empty referral code provided');
      return false;
    }
    
    // Normalize to lowercase for case-insensitive comparison
    const normalizedCode = referralCode.toLowerCase().trim();
    
    // Basic format validation
    if (!/^[a-z0-9]{4,20}$/i.test(normalizedCode)) {
      console.error('Invalid referral code format:', normalizedCode);
      return false;
    }
    
    console.log('Validating referral code:', normalizedCode);
    
    const { data, error } = await supabase
      .from('affiliates')
      .select('id, status')
      .ilike('referral_code', normalizedCode)
      .eq('status', 'approved')
      .maybeSingle();
      
    if (error) {
      console.error('Database error validating referral code:', error);
      throw error;
    }
    
    const isValid = !!data;
    console.log('Referral code validation result:', isValid, data);
    
    return isValid;
  } catch (error) {
    console.error('Error validating referral code:', error);
    return false;
  }
};

/**
 * Get affiliate name by referral code
 * @returns Promise<string|null> - affiliate name if found, null otherwise
 */
export const getAffiliateNameByCode = async (referralCode: string): Promise<string | null> => {
  try {
    if (!referralCode) return null;
    
    // Normalize to lowercase for case-insensitive comparison
    const normalizedCode = referralCode.toLowerCase().trim();
    
    const { data, error } = await supabase
      .rpc('get_affiliate_name_by_code', { 
        p_referral_code: normalizedCode
      });
      
    if (error) throw error;
    return data || null;
  } catch (error) {
    console.error('Error getting affiliate name:', error);
    return null;
  }
};

/**
 * Record a click on a referral link for analytics
 */
export const recordReferralClick = async (referralCode: string): Promise<boolean> => {
  try {
    if (!referralCode) return false;
    
    // Normalize to lowercase for case-insensitive comparison
    const normalizedCode = referralCode.toLowerCase().trim();
    
    // First get the affiliate data
    const { data: affiliateData, error: affiliateError } = await supabase
      .from('affiliates')
      .select('id')
      .ilike('referral_code', normalizedCode)
      .maybeSingle();
      
    if (affiliateError || !affiliateData) {
      console.error('Error finding affiliate:', affiliateError);
      return false;
    }
    
    // Get UTM parameters if available
    const urlParams = new URLSearchParams(window.location.search);
    const utmSource = urlParams.get('utm_source');
    const utmMedium = urlParams.get('utm_medium');
    const utmCampaign = urlParams.get('utm_campaign');
    
    const { error } = await supabase
      .from('affiliate_clicks')
      .insert({
        affiliate_id: affiliateData.id,
        referral_code: normalizedCode,
        // Anonymize IP for privacy
        ip_hash: await hashIp(),
        user_agent: navigator.userAgent.substring(0, 255),
        utm_source: utmSource,
        utm_medium: utmMedium,
        utm_campaign: utmCampaign
      });
      
    if (error) throw error;
    
    // Track the event for analytics
    await trackSubscriptionEvent('referral_click', {
      referral_code: normalizedCode,
      source_page: window.location.pathname,
      requires_auth: false
    });
    
    return true;
  } catch (error) {
    console.error('Error recording referral click:', error);
    return false;
  }
};

// Helper to hash IP address for privacy
async function hashIp(): Promise<string> {
  // This is just a placeholder - in a real implementation, the IP would be hashed server-side
  // since we don't have access to the client IP on the frontend
  return 'anonymous';
}

/**
 * Attribute a referral to a subscription
 */
export const attributeReferralToSubscription = async (subscriptionId: string, userId: string): Promise<boolean> => {
  try {
    // Get the stored referral code
    const referralCode = getStoredReferralCode();
    
    if (!referralCode) {
      console.log('No referral code stored to attribute');
      return false;
    }
    
    console.log('Attempting to attribute referral code to subscription:', referralCode, subscriptionId);
    
    // Call the attribute-referral edge function
    const { data, error } = await supabase.functions.invoke('attribute-referral', {
      body: { 
        referralCode, 
        subscriptionId,
        userId
      }
    });
    
    if (error) {
      console.error('Error attributing referral to subscription:', error);
      return false;
    }
    
    if (data?.success) {
      console.log('Successfully attributed referral to subscription:', data);
      
      // Track the successful attribution
      await trackSubscriptionEvent('referral_attributed', {
        referral_code: referralCode,
        requires_auth: true
      });
      
      // Clear the stored referral code after successful attribution
      clearReferralData();
      return true;
    } else {
      console.error('Failed to attribute referral to subscription:', data?.error || 'Unknown error');
      return false;
    }
  } catch (error) {
    console.error('Exception attributing referral to subscription:', error);
    return false;
  }
};

/**
 * Check if a user was referred (has referral code in their profile)
 */
export const checkUserWasReferred = async (userId: string): Promise<string | null> => {
  try {
    const { data: userData, error } = await supabase
      .from('profiles')
      .select('metadata')
      .eq('id', userId)
      .single();
      
    if (error || !userData) return null;
    
    // Check if user has referral code in their metadata
    const referralCode = userData.metadata?.referral_code;
    return referralCode || null;
  } catch (error) {
    console.error('Error checking if user was referred:', error);
    return null;
  }
};
